import { PaginatedApiResponse } from '../models/securepay';

/**
 * Standard API envelope for chargeback merchant notifications.
 * `message` may be null on success responses.
 */
export interface NotificationApiEnvelope<T> {
  success: boolean;
  statusCode: string;
  message: string | null;
  data: T;
}

/** Single notification record (normalized for UI). */
export interface SecurepayNotification {
  id: string;
  message: string;
  createdAt: string;
  /** Plain-language title shown in the drawer (may match API `title` or a derived line from `message`). */
  title: string;
  /** Transaction / dispute reference when known. */
  reference: string | null;
  /** Display string for the amount column, e.g. ₦9,000.00 */
  amountDisplay: string;
  /** When true, Accept / Decline actions are shown. */
  requiresAction: boolean;
  /** Id used for accept/decline mutations when available. */
  transactionId: string | null;
  metadata?: any;
}

/** Wire-format row as returned by the backend (field names kept permissive for forward compatibility). */
export interface NotificationApiRecord {
  id?: string | number | null;
  notificationId?: string | null;
  message?: string | null;
  title?: string | null;
  createdAt?: string | null;
  reference?: string | null;
  referenceId?: string | null;
  transactionRef?: string | null;
  amount?: number | null;
  requiresAction?: boolean | null;
  transactionId?: string | null;
  chargeBackId?: string | null;
}

export type NotificationListHttpResponse = NotificationApiEnvelope<
  PaginatedApiResponse<NotificationApiRecord[]>
>;

export interface NotificationPaginationState {
  page: number;
  pageSize: number;
  totalPages: number;
  recordsTotal: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

const NAIRA_IN_MESSAGE =
  /(?:₦|NGN)\s*([\d]{1,3}(?:,\d{3})*(?:\.\d{2})?|\d+(?:\.\d{2})?)/i;

const REF_PATTERN = /\b([A-Z0-9]{8,}(?:HD|[A-Z0-9])*)\b/;

export function parseNairaFromMessage(
  message: string | null | undefined,
): string {
  if (!message?.trim()) {
    return '—';
  }
  const direct = message.match(NAIRA_IN_MESSAGE);
  if (direct) {
    const num = direct[1]?.replace(/,/g, '') ?? '';
    const n = Number(num);
    if (!Number.isNaN(n)) {
      return `₦${n.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return direct[0].replace(/NGN/i, '₦').trim();
  }
  return '—';
}

export function parseReferenceFromMessage(
  message: string | null | undefined,
  explicit?: string | null,
): string | null {
  if (explicit?.trim()) {
    return explicit.trim();
  }
  if (!message?.trim()) {
    return null;
  }
  const m = message.match(REF_PATTERN);
  return m?.[1] ?? null;
}

export function deriveTitleFromMessage(
  message: string | null | undefined,
  explicitTitle?: string | null,
): string {
  if (explicitTitle?.trim()) {
    return explicitTitle.trim();
  }
  if (!message?.trim()) {
    return 'Notification';
  }
  const firstLine = message.split(/\r?\n/).map((l) => l.trim())[0] ?? message;
  return firstLine || 'Notification';
}

export function deriveRequiresAction(
  record: NotificationApiRecord,
  titleFallback: string,
  messageFallback: string,
): boolean {
  if (typeof record.requiresAction === 'boolean') {
    return record.requiresAction;
  }
  const text = `${titleFallback} ${messageFallback}`.toLowerCase();
  return /chargeback|dispute|debited|action required|requires?\s+response/.test(
    text,
  );
}

export function mapNotificationRecord(record: any): SecurepayNotification {
  const message = record.message ?? '';
  const title = deriveTitleFromMessage(message, record.title);
  const reference =
    record.reference?.trim() ||
    record.referenceId?.trim() ||
    record.transactionRef?.trim() ||
    parseReferenceFromMessage(message, null);
  const amountDisplay =
    record.amount != null && !Number.isNaN(Number(record.amount))
      ? `₦${Number(record.amount).toLocaleString('en-NG', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : parseNairaFromMessage(message);
  const idBase =
    record.id ??
    record.notificationId ??
    reference ??
    `${record.createdAt ?? ''}-${message.slice(0, 32)}`;
  const id = String(idBase);
  const createdAt = record.createdAt ?? new Date(0).toISOString();
  const requiresAction = deriveRequiresAction(record, title, message);
  const transactionId =
    record.transactionId?.trim() ||
    record.chargeBackId?.trim() ||
    record.id?.toString().trim() ||
    null;

  return {
    id,
    message,
    createdAt,
    title,
    reference,
    amountDisplay,
    requiresAction,
    transactionId,
    metadata: record.metadata,
  };
}
